/**
 * Sound utility for playing notification sounds
 * Handles browser autoplay restrictions gracefully
 * Respects user preferences for sound notifications
 */

import { useUserPreferencesStore } from '@/store/user-preferences.store'

import {
  isBrowserNotificationPreferences,
  PreferenceContextEnum,
} from '@scrambl/shared'

let soundVolume = 0.5

/**
 * Create audio only when a sound genuinely needs to play. Constructing and
 * preloading media during app startup can make Bluetooth headphones switch
 * their active connection to the desktop even though nothing is audible.
 */
function createSound(src: string): HTMLAudioElement | null {
  if (typeof window === 'undefined') return null

  const sound = new Audio(src)
  sound.volume = soundVolume
  return sound
}

/**
 * Do not let a background socket event create Chrome's first media session.
 * Besides being blocked by autoplay policy, that attempted playback can make
 * Bluetooth headphones hand off to the computer while the page is loading.
 */
function hasUserActivation(): boolean {
  if (typeof navigator === 'undefined') return false
  return navigator.userActivation?.hasBeenActive ?? true
}

/** Release Chrome's media player as soon as the short notification ends. */
function releaseSound(sound: HTMLAudioElement): void {
  sound.pause()
  sound.removeAttribute('src')
  sound.load()
}

function playSound(src: string): void {
  if (!hasUserActivation()) return

  const sound = createSound(src)
  if (!sound) return

  let released = false
  const release = () => {
    if (released) return
    released = true
    releaseSound(sound)
  }

  sound.addEventListener('ended', release, { once: true })
  sound.addEventListener('error', release, { once: true })
  void sound.play().catch(release)
}

/**
 * Check if sounds are enabled in user preferences
 * Defaults to true if no preference is set
 */
function isSoundEnabled(): boolean {
  const { getPreference } = useUserPreferencesStore.getState()
  const preference = getPreference(PreferenceContextEnum.NOTIFICATIONS)

  if (
    preference?.preferences &&
    isBrowserNotificationPreferences(preference.preferences)
  ) {
    // Default to true if playSound is not explicitly set
    return preference.preferences.playSound ?? true
  }

  // Default to enabled if no preference exists
  return true
}

/**
 * Play the new message sound (for thread messages)
 * Respects user preference for sound notifications
 * Catches autoplay restrictions silently
 */
export function playNewMessageSound(): void {
  if (!isSoundEnabled()) return
  playSound('/sounds/new-message.mp3')
}

/**
 * Play the new notification sound (for notification widget)
 * Respects user preference for sound notifications
 * Catches autoplay restrictions silently
 */
export function playNewNotificationSound(): void {
  if (!isSoundEnabled()) return
  playSound('/sounds/new-notification.mp3')
}

/**
 * A short rising chime for an unlock moment (badge, streak, weekly goal).
 * Synthesized with Web Audio — no asset to load — and gated like every other
 * sound on the user's playSound preference and prior page interaction.
 */
export function playUnlockSound(): void {
  if (!isSoundEnabled() || !hasUserActivation()) return
  if (typeof window === 'undefined') return
  const AudioContextClass =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext
  if (!AudioContextClass) return
  try {
    const context = new AudioContextClass()
    const start = context.currentTime + 0.02
    // C6, E6, G6 — a bright major arpeggio
    const notes = [1046.5, 1318.5, 1568]
    notes.forEach((frequency, index) => {
      const oscillator = context.createOscillator()
      const gain = context.createGain()
      const at = start + index * 0.09
      oscillator.type = 'sine'
      oscillator.frequency.setValueAtTime(frequency, at)
      gain.gain.setValueAtTime(0, at)
      gain.gain.linearRampToValueAtTime(soundVolume * 0.35, at + 0.015)
      gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.45)
      oscillator.connect(gain).connect(context.destination)
      oscillator.start(at)
      oscillator.stop(at + 0.5)
    })
    // Release the audio device once the chime has finished.
    window.setTimeout(() => void context.close().catch(() => {}), 1200)
  } catch {
    // Audio is decorative.
  }
}

/**
 * Set volume for all sounds (0.0 to 1.0)
 */
export function setSoundVolume(volume: number): void {
  soundVolume = Math.max(0, Math.min(1, volume))
}
